/**
 * Roads & Sewers BOQ Summary Computer
 *
 * Computes all Roads & Sewers BOQ summary sections (quantities, rates, amounts)
 * from live tender state + item library, for persistence in
 * billOfQuantities[0].roadAndSewers.summary — the R&S counterpart of
 * computeGroundworksBoqSummary (groundworksBoqSummaryComputer.ts). Every
 * section here recomputes the exact same figures each domain's own Bill
 * Summary page / useRoadsAndSewersTotalSync.ts already derives live, so the
 * persisted payload can never drift from what's on screen.
 */

import { ItemLibraryResponse } from "../api/itemLibrary";
import { TenderState, PumpChamberConfig, PrelimsItemRecord, CompoundConfig, FoulBuildUpRowData } from "../types/tender.types";
import type { BoqSummaryLineItem } from "./groundworksBoqSummaryComputer";
import { buildFoulSummaryData, buildBuildUpSummaryData, type FoulSummaryRow } from "./foulSummaryUtils";
import { buildStormSummaryData, buildStormBuildUpSummaryData, type StormSummaryRow } from "./stormSummaryUtils";
import { PUMP_STATION_ROWS, RISING_MAIN_ROWS, PUMP_STATION_GENERAL_SECTION, applyPumpStationLadderType } from "./foulBuildUpRows";
import { getPondsRows, DEFAULT_POND_SECTIONS } from "./stormBuildUpPonds";
import { getSwalesRows, DEFAULT_SWALE_SECTIONS } from "./stormBuildUpSwales";
import { HEADWALL_ROWS } from "./stormBuildUpHeadwalls";
import { withDashedTitleSuffix } from "./stormBuildUpTitles";
import { getCellularAttenuationRows, DEFAULT_CELLULAR_SECTIONS } from "./stormBuildUpCellularAttenuation";
import { getWeholiteAttenuationRows, DEFAULT_WEHOLITE_SECTIONS } from "./stormBuildUpWeholiteAttenuation";
import { getConcAttenuationRows, DEFAULT_CONC_SECTIONS } from "./stormBuildUpConcAttenuation";
import { getBoxCulvertsRows, DEFAULT_BOX_CULVERT_SECTIONS } from "./stormBuildUpBoxCulverts";
import { findPondsSwalesEvaluateCode, findHeadwallEvaluateCode } from "./stormPondsSwalesEvaluateCodesMapping";
import { findCellularAttenuationEvaluateCode } from "./stormCellularAttenuationEvaluateCodesMapping";
import { findWeholiteAttenuationEvaluateCode } from "./stormWeholiteAttenuationEvaluateCodesMapping";
import { resolveConcAttenuationEvaluateCode } from "./stormConcAttenuationEvaluateCodesMapping";
import { findBoxCulvertsEvaluateCode } from "./stormBoxCulvertsEvaluateCodesMapping";
import type { StormBuildUpRowDef } from "./stormBuildUpTypes";
import { buildRoadsBillSummarySections } from "./roadsSummaryCalculations";
import { MAIN_SERVICE_TRENCHING_MAPPING } from "./mainServiceTrenchingMapping";
import { findMainServiceTrenchingEvaluateCode } from "./mainServicesEvaluateCodesMapping";
import { SUBSTATION_BASE_ROW_DEFS } from "./substationBaseRowDefs";
import { findSubstationBaseEvaluateCode } from "./substationBaseEvaluateCodesMapping";
import { getPOSRowDefs, getPOSConfigurableField, DEFAULT_POS_SECTIONS, isPosGeotextileExcluded } from "./posRowDefs";
import { findPosEvaluateCode } from "./posEvaluateCodesMapping";
import { COMPOUND_ROW_DEFS, getCompoundLabels } from "./compoundRowDefs";
import { findCompoundEvaluateCode } from "./compoundEvaluateCodesMapping";
import { PRELIMS_MAPPING, LEGACY_METHOD_RELATED_GROUP_IDS } from "./prelimsMapping";
import { computeRoadsAndSewersMarkupFactor } from "../hooks/useRoadsAndSewersMarkupFactor";

export interface RoadsAndSewersBoqSummary {
  foulSewers: BoqSummaryLineItem[];
  pumpStation: BoqSummaryLineItem[];
  risingMain: BoqSummaryLineItem[];
  stormSewers: BoqSummaryLineItem[];
  pondsSwales: BoqSummaryLineItem[];
  cellularAttenuation: BoqSummaryLineItem[];
  weholiteAttenuation: BoqSummaryLineItem[];
  concAttenuation: BoqSummaryLineItem[];
  boxCulverts: BoqSummaryLineItem[];
  roads: BoqSummaryLineItem[];
  mainServices: BoqSummaryLineItem[];
  substation: BoqSummaryLineItem[];
  pos: BoqSummaryLineItem[];
  prelims: BoqSummaryLineItem[];
  compound: BoqSummaryLineItem[];
}

const readDisplayFlag = (key: string): boolean => {
  try {
    const saved = localStorage.getItem(key);
    return saved !== null ? (JSON.parse(saved) as boolean) : false;
  } catch {
    return false;
  }
};

const round2 = (n: number): number => parseFloat((n || 0).toFixed(2));
const roundQty = (n: number): number => Math.abs(Math.round(n || 0));

const sumChildrenAmount = (node: BoqSummaryLineItem): number => {
  if (!node.children || node.children.length === 0) return node.amount || 0;
  const total = node.children.reduce((s, c) => s + sumChildrenAmount(c), 0);
  node.amount = round2(total);
  return node.amount;
};

/** Recursively flattens a FoulSummaryRow/StormSummaryRow tree (built by
 * buildFoulSummaryData/buildStormSummaryData/buildBuildUpSummaryData/
 * buildStormBuildUpSummaryData — the same trees each domain's own Bill
 * Summary table + useRoadsAndSewersTotalSync.ts render/sum) into
 * BoqSummaryLineItem shape. A row with children becomes a section (amount =
 * sum of its children); a leaf row's amount = qty × rate resolved via
 * `getRate(evaluateCode)`. */
function convertSummaryRow(
  row: FoulSummaryRow | StormSummaryRow,
  category: string,
  getRate: (code?: string) => number,
  keyPrefix: string,
): BoqSummaryLineItem {
  if (row.children && row.children.length > 0) {
    const children = row.children.map((c, i) => convertSummaryRow(c, category, getRate, `${keyPrefix}-${i}`));
    return {
      key: row.key || keyPrefix,
      fieldName: row.key || keyPrefix,
      // A 'pipe-type' group header (buildBuildUpSummaryData/
      // buildStormBuildUpSummaryData — e.g. "PUMP CHAMBER", "Tank 1",
      // "Culvert 2") is built with its label on `reference`, not
      // `description` — FoulBuildUpBillSummary.tsx/StormBuildUpBillSummary.tsx
      // render `record.reference` for these rows on screen (see their
      // `rowType === 'pipe-type'` branch), while a top-level 'section' header
      // (buildFoulSummaryData's "Drainage Runs"/"Manholes") sets `description`
      // directly. `row.description` alone was blank for every 'pipe-type'
      // header, so the exported sheet showed no category label at all for
      // these groups even though the on-screen table shows one.
      description: row.description || row.reference || '',
      quantity: 0,
      unit: '',
      rate: 0,
      amount: round2(children.reduce((s, c) => s + (c.amount || 0), 0)),
      category,
      children,
    };
  }
  // Amount is derived from the RAW (unrounded) quantity — only the `quantity`
  // field itself (Qty column display / persisted count) rounds to a whole
  // number. Multiplying by the rounded quantity instead would silently lose
  // precision every live Bill Summary component's own Amount column avoids
  // (they all compute qty × rate from the raw parsed quantity).
  const rawQuantity = parseFloat(row.quantity ?? '') || 0;
  const quantity = roundQty(rawQuantity);
  const rate = row.evaluateCode ? round2(getRate(row.evaluateCode)) : 0;
  return {
    key: row.key || keyPrefix,
    fieldName: row.key || keyPrefix,
    description: row.description || '',
    quantity,
    unit: row.unit || '',
    rate,
    amount: round2(rawQuantity * rate),
    category,
    resourceCode: row.evaluateCode,
  };
}

function convertSummaryTree(
  rows: (FoulSummaryRow | StormSummaryRow)[],
  category: string,
  getRate: (code?: string) => number,
): BoqSummaryLineItem[] {
  return rows.map((r, i) => convertSummaryRow(r, category, getRate, `${category}-${i}`));
}

function computeRoadsSection(
  roadsData: TenderState['roads'] | undefined,
  getRate: (code?: string) => number,
): BoqSummaryLineItem[] {
  const sections = buildRoadsBillSummarySections(roadsData);
  return sections.map((section) => {
    const children: BoqSummaryLineItem[] = section.children.map((row) => {
      const qty = Object.values(row.values).reduce((s, v) => s + (v.qty ?? 0), 0);
      const roundedQty = roundQty(qty);
      const rate = row.evaluateCode ? round2(getRate(row.evaluateCode)) : 0;
      return {
        key: row.key,
        fieldName: row.key,
        description: row.description,
        quantity: roundedQty,
        unit: row.unit,
        rate,
        amount: round2(qty * rate),
        category: section.description,
        resourceCode: row.evaluateCode,
      };
    });
    const parent: BoqSummaryLineItem = {
      key: section.key,
      fieldName: section.key,
      description: section.description,
      quantity: 0,
      unit: '',
      rate: 0,
      amount: 0,
      category: section.description,
      children,
    };
    sumChildrenAmount(parent);
    return parent;
  });
}

function computeMainServicesSection(
  quantities: Record<string, Record<string, number>> | undefined,
  getRate: (code?: string) => number,
): BoqSummaryLineItem[] {
  const table = quantities || {};
  return MAIN_SERVICE_TRENCHING_MAPPING.map((group) => {
    const children: BoqSummaryLineItem[] = group.items.map((item) => {
      const roadQtys = table[item.id] || {};
      const totalQty = Object.values(roadQtys).reduce((s, q) => s + (q || 0), 0);
      const roundedQty = roundQty(totalQty);
      const code = findMainServiceTrenchingEvaluateCode(item.description);
      const rate = round2(getRate(code));
      return {
        key: item.id,
        fieldName: item.id,
        description: item.description,
        quantity: roundedQty,
        unit: item.unit,
        rate,
        amount: round2(totalQty * rate),
        category: group.label,
        resourceCode: code,
      };
    });
    const parent: BoqSummaryLineItem = {
      key: group.id,
      fieldName: group.id,
      description: group.label,
      quantity: 0,
      unit: '',
      rate: 0,
      amount: 0,
      category: group.label,
      children,
    };
    sumChildrenAmount(parent);
    return parent;
  });
}

function computeSubstationSection(
  savedData: Record<string, FoulBuildUpRowData> | undefined,
  getRate: (code?: string) => number,
): BoqSummaryLineItem[] {
  const table = savedData || {};
  const result: BoqSummaryLineItem[] = [];
  let currentSection: BoqSummaryLineItem | null = null;
  SUBSTATION_BASE_ROW_DEFS.forEach((row) => {
    if (row.rowType === 'sectionHeader') {
      currentSection = {
        key: row.id, fieldName: row.id, description: row.description,
        quantity: 0, unit: '', rate: 0, amount: 0, category: row.description, children: [],
      };
      result.push(currentSection);
      return;
    }
    const qty = table[row.id]?.quant ?? 0;
    const roundedQty = roundQty(qty);
    const code = findSubstationBaseEvaluateCode(row.description);
    const rate = round2(getRate(code));
    const item: BoqSummaryLineItem = {
      key: row.id, fieldName: row.id, description: row.description,
      quantity: roundedQty, unit: row.unit, rate, amount: round2(qty * rate),
      category: currentSection?.description || '', resourceCode: code,
    };
    currentSection?.children?.push(item);
  });
  result.forEach(sumChildrenAmount);
  return result;
}

function computePosSection(
  posState: TenderState['pos'],
  getRate: (code?: string) => number,
): BoqSummaryLineItem[] {
  const sectionCount = posState?.config?.sectionCount ?? DEFAULT_POS_SECTIONS;
  const rowThicknessMm = posState?.config?.rowThicknessMm ?? {};
  const rowText = posState?.config?.rowText ?? {};
  const table = posState?.table ?? {};
  const geotextileIncluded = posState?.config?.geotextileIncluded ?? {};
  const sectionTitles = posState?.config?.sectionTitles ?? {};
  const rows = getPOSRowDefs(sectionCount);

  const result: BoqSummaryLineItem[] = [];
  let currentTop: BoqSummaryLineItem | null = null;
  let currentSub: BoqSummaryLineItem | null = null;

  rows.forEach((row) => {
    if (row.rowType === 'empty') return;
    if (row.rowType === 'posHeader') {
      const headerDescription = sectionTitles[row.id] ? `${row.description} - ${sectionTitles[row.id]}` : row.description;
      currentTop = {
        key: row.id, fieldName: row.id, description: headerDescription,
        quantity: 0, unit: '', rate: 0, amount: 0, category: headerDescription, children: [],
      };
      result.push(currentTop);
      currentSub = null;
      return;
    }
    if (row.rowType === 'subHeader' || row.rowType === 'leapHeader') {
      currentSub = {
        key: row.id, fieldName: row.id, description: row.description,
        quantity: 0, unit: '', rate: 0, amount: 0, category: currentTop?.description || '', children: [],
      };
      currentTop?.children?.push(currentSub);
      return;
    }
    if (isPosGeotextileExcluded(row.id, geotextileIncluded)) return;
    const qty = table[row.id]?.quant ?? 0;
    const roundedQty = roundQty(qty);
    const configurableField = getPOSConfigurableField(row.id);
    const mm = configurableField ? rowThicknessMm[row.id] : undefined;
    const customText = configurableField ? rowText[row.id] : undefined;
    const description = configurableField && (mm || customText)
      ? configurableField.describe(mm ?? configurableField.placeholderMm ?? 'xx', customText)
      : row.description;
    const code = findPosEvaluateCode(description);
    const rate = round2(getRate(code));
    const item: BoqSummaryLineItem = {
      key: row.id, fieldName: row.id, description,
      quantity: roundedQty, unit: row.unit, rate, amount: round2(qty * rate),
      category: currentSub?.description || currentTop?.description || '', resourceCode: code,
    };
    (currentSub ?? currentTop)?.children?.push(item);
  });

  result.forEach(sumChildrenAmount);
  return result;
}

function computeCompoundSection(
  compoundState: TenderState['compound'],
  getRate: (code?: string) => number,
): BoqSummaryLineItem[] {
  const config = (compoundState?.config ?? {}) as CompoundConfig;
  const table = compoundState?.table ?? {};
  const { sectionLabels, itemLabels } = getCompoundLabels(config);

  const result: BoqSummaryLineItem[] = [];
  let currentHeader: BoqSummaryLineItem | null = null;
  let currentSection: BoqSummaryLineItem | null = null;

  COMPOUND_ROW_DEFS.forEach((row) => {
    if (row.rowType === 'compoundHeader') {
      currentHeader = {
        key: row.id, fieldName: row.id, description: row.description,
        quantity: 0, unit: '', rate: 0, amount: 0, category: row.description, children: [],
      };
      result.push(currentHeader);
      currentSection = null;
      return;
    }
    if (row.rowType === 'sectionHeader' || row.rowType === 'subHeader') {
      const label = sectionLabels[row.id] ?? row.description;
      currentSection = {
        key: row.id, fieldName: row.id, description: label,
        quantity: 0, unit: '', rate: 0, amount: 0, category: currentHeader?.description || '', children: [],
      };
      currentHeader?.children?.push(currentSection);
      return;
    }
    const qty = table[row.id]?.quant ?? 0;
    const roundedQty = roundQty(qty);
    const description = itemLabels[row.id] ?? row.description;
    const code = findCompoundEvaluateCode(description);
    const rate = round2(getRate(code));
    const item: BoqSummaryLineItem = {
      key: row.id, fieldName: row.id, description,
      quantity: roundedQty, unit: row.unit, rate, amount: round2(qty * rate),
      category: currentSection?.description || currentHeader?.description || '', resourceCode: code,
    };
    (currentSection ?? currentHeader)?.children?.push(item);
  });

  result.forEach(sumChildrenAmount);
  return result;
}

function computePrelimsSection(
  state: TenderState,
  itemLibraryData: ItemLibraryResponse | undefined,
): BoqSummaryLineItem[] {
  const resourceCodeMap = new Map<string, { nettRate: number; baseRate: number }>();
  (itemLibraryData ?? []).forEach((item) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) resourceCodeMap.set(code, { nettRate: item.nettRate, baseRate: item.baseRate });
  });
  const savedItemsMap = new Map<string, PrelimsItemRecord>();
  (state.preliminaryItems?.preliminaryItems || []).forEach((saved) => savedItemsMap.set(saved.id, saved));

  const roadsAndSewersSettings = state.globalSettings?.roadsAndSewers;
  const markupFactor = computeRoadsAndSewersMarkupFactor(
    roadsAndSewersSettings?.overheadAndProfit,
    roadsAndSewersSettings?.preliminaries,
    readDisplayFlag("roadsAndSewers_includeOhpDisplay"),
    readDisplayFlag("roadsAndSewers_includePreliminariesDisplay"),
  );

  const buildItemLine = (
    item: { description: string; unit: string; code: string; variants?: { label: string; code: string }[] },
    id: string,
    category: string,
    legacyId?: string,
  ): BoqSummaryLineItem => {
    const saved = savedItemsMap.get(id) ?? (legacyId ? savedItemsMap.get(legacyId) : undefined);
    // Variant rows (e.g. "CBR testing option(s)") resolve their code from the
    // user's saved selection, same as PrelimsBillSummary.tsx's own resolution
    // (and sumPrelimsAmount in prelimsMapping.ts) — item.code alone would price
    // every variant at its category's default rate instead of the one actually
    // selected, so the persisted BOQ could disagree with what the Bill Summary
    // itself displays and save with the live Sum Analysis for the same row.
    const selectedVariant = item.variants?.find((v) => v.code === saved?.code);
    const code = item.variants ? (selectedVariant?.code ?? item.code) : item.code;
    const libItem = resourceCodeMap.get((code || '').trim().toUpperCase());
    const libRate = libItem ? (libItem.nettRate || libItem.baseRate || 0) : 0;
    const qty = saved?.quantity ?? 0;
    const baseRate = (saved?.rate ?? 0) > 0 ? (saved!.rate) : libRate;
    const rate = round2(baseRate * markupFactor);
    const roundedQty = roundQty(qty);
    return {
      key: id, fieldName: id, description: saved?.description || item.description,
      quantity: roundedQty, unit: item.unit, rate, amount: round2(qty * rate),
      category, resourceCode: saved?.code || item.code,
    };
  };

  return PRELIMS_MAPPING.map((cat) => {
    const children: BoqSummaryLineItem[] = [];
    (cat.items ?? []).forEach((item) => {
      const idSuffix = item.description.replace(/[^a-zA-Z0-9]/g, '-');
      const id = `prelim-${cat.id}-${idSuffix}`;
      const legacyGroupId = LEGACY_METHOD_RELATED_GROUP_IDS[item.description];
      const legacyId = legacyGroupId ? `prelim-${cat.id}-${legacyGroupId}-${idSuffix}` : undefined;
      children.push(buildItemLine(item, id, cat.label, legacyId));
    });
    // A category with exactly one named group and no root items carries no
    // real grouping value — fold its items straight into the category
    // instead of a redundant single group row, exactly matching
    // PrelimsBillSummary.tsx's own `foldSoleGroup` on-screen rule. Without
    // this, the export always added a group-header node the on-screen table
    // never shows for these categories, throwing off both the row structure
    // and (via `addLiveDomainSheet`'s `walk`) the "Item" column's numbering.
    const foldSoleGroup = cat.groups.length === 1 && (cat.items?.length ?? 0) === 0;
    if (foldSoleGroup) {
      cat.groups[0].items.forEach((item) => {
        const id = `prelim-${cat.id}-${cat.groups[0].id}-${item.description.replace(/[^a-zA-Z0-9]/g, '-')}`;
        children.push(buildItemLine(item, id, cat.label));
      });
    } else {
      cat.groups.forEach((grp) => {
        const groupChildren: BoqSummaryLineItem[] = grp.items.map((item) => {
          const id = `prelim-${cat.id}-${grp.id}-${item.description.replace(/[^a-zA-Z0-9]/g, '-')}`;
          return buildItemLine(item, id, grp.label);
        });
        const groupNode: BoqSummaryLineItem = {
          key: `prelim-${cat.id}-${grp.id}`, fieldName: grp.id, description: grp.label,
          quantity: 0, unit: '', rate: 0, amount: 0, category: cat.label, children: groupChildren,
        };
        sumChildrenAmount(groupNode);
        children.push(groupNode);
      });
    }
    const catNode: BoqSummaryLineItem = {
      key: cat.id, fieldName: cat.id, description: cat.label,
      quantity: 0, unit: '', rate: 0, amount: 0, category: cat.label, children,
    };
    sumChildrenAmount(catNode);
    return catNode;
  });
}

/**
 * Computes every Roads & Sewers Bill Summary domain's line-item summary from
 * live tender state, for persistence in billOfQuantities[0].roadAndSewers.summary
 * — mirrors computeGroundworksBoqSummary's role for groundworks.summary.
 */
export function computeRoadsAndSewersBoqSummary(
  state: TenderState,
  itemLibraryData: ItemLibraryResponse | undefined,
): RoadsAndSewersBoqSummary {
  // Same OH&P/Preliminaries markup every R&S Bill Summary/Sum Analysis total
  // now applies to its own rows — every non-Prelims domain here was
  // previously nett-only, which silently disagreed with the live (now
  // marked-up) Bill Summary pages once OH&P/Preliminaries was non-zero.
  const roadsAndSewersSettings = state.globalSettings?.roadsAndSewers;
  const markupFactor = computeRoadsAndSewersMarkupFactor(
    roadsAndSewersSettings?.overheadAndProfit,
    roadsAndSewersSettings?.preliminaries,
    readDisplayFlag("roadsAndSewers_includeOhpDisplay"),
    readDisplayFlag("roadsAndSewers_includePreliminariesDisplay"),
  );
  // Same flat rateMap convention as useRoadsAndSewersTotalSync.ts — nettRate
  // only (no baseRate fallback), so persisted amounts can never disagree with
  // the live Sum Analysis / Bill Summary totals. Marked up (and rounded to
  // 2dp) here so every `round2(getRate(...))` call site downstream gets the
  // exact same marked-up rate the live pages show — round2 on an
  // already-rounded value is a no-op, so double-rounding is safe.
  const rateMap: Record<string, number> = {};
  (itemLibraryData ?? []).forEach((item) => {
    if (item.resourceCode) rateMap[item.resourceCode] = round2((item.nettRate || 0) * markupFactor);
  });
  const getRate = (code?: string): number => (code ? rateMap[code] ?? 0 : 0);
  // A few Foul Sewers/Pump Station/Rising Main build-up evaluate codes are
  // combined (e.g. "50mm Gravel & Geotextile Areas" → "ALP050+ALP031") — sum
  // whichever parts resolve to a library rate, same convention as
  // useRoadsAndSewersTotalSync.ts's getFoulLibRate. Previously these went
  // through the plain `getRate` above, which does a direct dictionary lookup
  // on the whole combined string — never a match — so the row's rate/amount
  // persisted as 0 instead of the summed parts.
  const getFoulRate = (code?: string): number => {
    if (!code) return 0;
    if (code.includes('+')) return code.split('+').reduce((sum, c) => sum + (rateMap[c.trim()] ?? 0), 0);
    return rateMap[code.trim()] ?? 0;
  };
  // Storm-only "+"-combined evaluate code convention (e.g. "ALPE010+L001B") —
  // same as useRoadsAndSewersTotalSync.ts's getStormLibRate.
  const getStormRate = (code?: string): number => {
    if (!code) return 0;
    if (code.includes('+')) return code.split('+').reduce((sum, c) => sum + (rateMap[c.trim()] ?? 0), 0);
    return rateMap[code.trim()] ?? 0;
  };
  // Roads and Main Services are the two domains whose own Bill Summary
  // (RoadsBillSummary.tsx's resourceCodeMap, MainServicesBillSummary.tsx's
  // rateMap) both normalizes its item-library lookup (trim+uppercase) and
  // falls back to baseRate when nettRate is 0/unset — unlike the plain
  // `rateMap` above (exact-match, nettRate only, matching every other
  // domain's own Bill Summary). Using `getRate` for these two previously
  // priced any code that only matched case/whitespace-insensitively, or any
  // code with no nettRate but a real baseRate, at £0 — silently
  // undercounting the persisted Roads/Main Services amounts relative to what
  // those Bill Summary tabs, and useRoadsAndSewersTotalSync.ts's own
  // roadsTotal/mainServicesTotal, actually show. Same normalizedRateMap
  // convention as that hook.
  const normalizedRateMap: Record<string, number> = {};
  (itemLibraryData ?? []).forEach((item) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) normalizedRateMap[code] = round2((item.nettRate || item.baseRate || 0) * markupFactor);
  });
  // A few Roads evaluate codes are combined too (e.g. "Tree pits" →
  // "ALP200+ALP201") — same "+"-combined convention as getFoulRate/getStormRate
  // above; sum whichever parts resolve. Previously missing here, so a
  // combined-code Roads row persisted priced at £0.
  const getNormalizedRate = (code?: string): number => {
    if (!code) return 0;
    const trimmed = code.trim();
    if (trimmed.includes('+')) {
      return trimmed.split('+').reduce((sum, c) => sum + (normalizedRateMap[c.trim().toUpperCase()] ?? 0), 0);
    }
    return normalizedRateMap[trimmed.toUpperCase()] ?? 0;
  };

  const foul = state.foulSewers;
  const foulRows = foul
    ? buildFoulSummaryData(
        foul.pipeRuns.table || [], foul.manholes.table || [], foul.lateralConnections.table || [],
        foul.pipeRuns.config.eoQtys, foul.manholes.config.manholeScheduleConfig,
      )
    : [];

  const storm = state.stormSewers;
  const stormRows = storm
    ? buildStormSummaryData(
        storm.pipeRuns.table || [], storm.manholes.table || [], storm.lateralConnections.table || [],
        storm.gullyConnections.table || [], storm.pipeRuns.config.eoQtys, storm.manholes.config.manholeScheduleConfig,
      )
    : [];

  const pumpChamberConfig = (state.pumpStation?.config as PumpChamberConfig)?.coverLevel !== undefined
    ? (state.pumpStation?.config as PumpChamberConfig)
    : undefined;
  const pumpStationRows = buildBuildUpSummaryData(applyPumpStationLadderType(PUMP_STATION_ROWS, state.foulSewers?.manholes?.config?.manholeScheduleConfig?.ladders, state.pumpStation?.config as PumpChamberConfig | undefined), state.pumpStation?.table, pumpChamberConfig, PUMP_STATION_GENERAL_SECTION);
  const risingMainRows = buildBuildUpSummaryData(RISING_MAIN_ROWS, state.risingMain?.table);

  const pondSectionCount = state.pondsSwales?.config?.pondSectionCount ?? DEFAULT_POND_SECTIONS;
  const exTopsoilMm = Math.round((state.roads?.cutFillSections?.config?.stripValueDefault ?? 0.3) * 1000);
  const pondTopsoilThicknessMm = state.pondsSwales?.config?.pondTopsoilThicknessMm ?? {};
  const swaleSectionCount = state.pondsSwales?.config?.swaleSectionCount ?? DEFAULT_SWALE_SECTIONS;
  const swaleTopsoilThicknessMm = state.pondsSwales?.config?.swaleTopsoilThicknessMm ?? {};
  const resolvePondsSwalesEvaluateCode = (row: StormBuildUpRowDef): string =>
    row.id.startsWith('hw_')
      ? findHeadwallEvaluateCode(row.description) ?? ''
      : findPondsSwalesEvaluateCode(row.description) ?? '';
  const pondsSwalesRowDefs: StormBuildUpRowDef[] = withDashedTitleSuffix([
    ...getPondsRows(pondSectionCount, exTopsoilMm, pondTopsoilThicknessMm, state.pondsSwales?.config?.sectionTitles),
    ...getSwalesRows(swaleSectionCount, exTopsoilMm, swaleTopsoilThicknessMm, state.pondsSwales?.config?.sectionTitles),
    ...HEADWALL_ROWS,
  ]);
  const pondsSwalesRows = buildStormBuildUpSummaryData(pondsSwalesRowDefs, state.pondsSwales?.table, resolvePondsSwalesEvaluateCode);

  const cellularSectionCount = state.cellularAttenuation?.config?.sectionCount ?? DEFAULT_CELLULAR_SECTIONS;
  const cellularRows = buildStormBuildUpSummaryData(
    withDashedTitleSuffix(getCellularAttenuationRows(cellularSectionCount, undefined, state.cellularAttenuation?.config?.crateDims, state.cellularAttenuation?.config?.sectionTitles)), state.cellularAttenuation?.table,
    (row) => findCellularAttenuationEvaluateCode(row.description) ?? '',
  );

  const weholiteSectionCount = state.weholiteAttenuation?.config?.sectionCount ?? DEFAULT_WEHOLITE_SECTIONS;
  const weholiteSupplierName = state.weholiteAttenuation?.config?.supplierName ?? 'Weholite';
  const weholiteConfigPct = state.weholiteAttenuation?.config?.weholiteConfigPct ?? {};
  const weholiteRows = buildStormBuildUpSummaryData(
    withDashedTitleSuffix(getWeholiteAttenuationRows(weholiteSectionCount, weholiteConfigPct, weholiteSupplierName, state.weholiteAttenuation?.config?.sectionTitles)), state.weholiteAttenuation?.table,
    (row) => findWeholiteAttenuationEvaluateCode(row.id) ?? '',
  );

  const concSectionCount = state.concAttenuation?.config?.sectionCount ?? DEFAULT_CONC_SECTIONS;
  const concConfigPct = state.concAttenuation?.config?.concConfigPct ?? {};
  const concSupplierName = state.concAttenuation?.config?.supplierName ?? 'Carlow';
  const concRows = buildStormBuildUpSummaryData(
    withDashedTitleSuffix(getConcAttenuationRows(concSectionCount, concConfigPct, concSupplierName, state.concAttenuation?.config?.sectionTitles, state.concAttenuation?.config?.drawingRefs)), state.concAttenuation?.table,
    (row) => resolveConcAttenuationEvaluateCode(row) ?? '',
  );

  const boxCulvertSectionCount = state.boxCulverts?.config?.sectionCount ?? DEFAULT_BOX_CULVERT_SECTIONS;
  const culvertConfigMm = state.boxCulverts?.config?.culvertConfigMm ?? {};
  const culvertBackfillType = state.boxCulverts?.config?.culvertBackfillType ?? {};
  const boxCulvertRows = buildStormBuildUpSummaryData(
    getBoxCulvertsRows(boxCulvertSectionCount, culvertConfigMm, culvertBackfillType), state.boxCulverts?.table,
    (row) => findBoxCulvertsEvaluateCode(row.description) ?? '',
  );

  return {
    foulSewers: convertSummaryTree(foulRows, 'Foul Sewers', getFoulRate),
    pumpStation: convertSummaryTree(pumpStationRows, 'Pump Station', getFoulRate),
    risingMain: convertSummaryTree(risingMainRows, 'Rising Main', getFoulRate),
    stormSewers: convertSummaryTree(stormRows, 'Storm Sewers', getStormRate),
    pondsSwales: convertSummaryTree(pondsSwalesRows, 'Ponds & Swales', getStormRate),
    cellularAttenuation: convertSummaryTree(cellularRows, 'Cellular Attenuation', getStormRate),
    weholiteAttenuation: convertSummaryTree(weholiteRows, 'Weholite Attenuation', getStormRate),
    concAttenuation: convertSummaryTree(concRows, 'Conc Attenuation', getStormRate),
    boxCulverts: convertSummaryTree(boxCulvertRows, 'Box Culverts', getStormRate),
    roads: computeRoadsSection(state.roads, getNormalizedRate),
    mainServices: computeMainServicesSection(state.mainServices?.table, getNormalizedRate),
    substation: computeSubstationSection(state.substation?.table, getRate),
    pos: computePosSection(state.pos, getRate),
    prelims: computePrelimsSection(state, itemLibraryData),
    compound: computeCompoundSection(state.compound, getRate),
  };
}

/** Grand total across every summary domain — for validating the persisted
 * summary against `roadsAndSewersTotals.grandTotal` if ever needed. */
export function computeGrandTotalFromRoadsAndSewersSummary(summary: RoadsAndSewersBoqSummary): number {
  const sumItems = (items: BoqSummaryLineItem[]): number =>
    items.reduce((s, i) => s + (i.children?.length ? sumItems(i.children) : (i.amount || 0)), 0);
  return Object.values(summary).reduce((s, items) => s + sumItems(items), 0);
}

/** Flattens a persisted domain's `BoqSummaryLineItem[]` tree (parent nodes
 * carry `children`, e.g. Roads/MainServices/Substation/POS/Prelims/Compound)
 * into a flat `key → item` map, so a Bill Summary table's dev-mode SummaryKey
 * column can look up a leaf row's persisted value by the same `key`
 * convertSummaryRow/computeRoadsSection etc. gave it — regardless of how
 * deep it sits in the persisted tree. */
export function flattenBoqSummaryLineItems(
  items: BoqSummaryLineItem[] | undefined,
): Map<string, BoqSummaryLineItem> {
  const map = new Map<string, BoqSummaryLineItem>();
  const visit = (nodes: BoqSummaryLineItem[]) => {
    nodes.forEach((node) => {
      map.set(node.key, node);
      if (node.children?.length) visit(node.children);
    });
  };
  if (items) visit(items);
  return map;
}
